import { addItemToBasket, BASKET_TYPES, removeItemFromBasket, selectBasketDeliveries, updateBasketItem, updateDelivery } from 'containers/OrderDetails/basketSlice'
import RentalProductPopup, { getRentalProductName } from './popups/RentalProductPopup'
import { durationLabel, formatWindowRange, getDefaultDuration, estimatePrice } from 'utils/shortTerm'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import DeliveryPopup from 'components/popups/DeliveryPopup'
import { useReduxDispatch, useReduxSelector } from 'store'
import placeholder from 'assets/img/placeholder.png'
import ArrowBtn from 'components/buttons/ArrowBtn'
import OpenShopPopup from './popups/OpenShopPopup'
import ShopItemPopup from './popups/ShopItemPopup'
import useTranslation from 'hooks/useTranslation'
import useShopParams from './hooks/useShopParams'
import ScannerPopup from './popups/ScannerPopup'
import usePopupState from 'hooks/usePopupState'
import useShopItems from './hooks/useShopItems'
import useShopData from './hooks/useShopData'
import { useHistory } from 'react-router-dom'
import { displayPrice } from 'utils/string'
import HeaderNumber from './HeaderNumber'
import Loading from 'components/Loading'
import Error from 'components/Error'
import './style.scss'

const type = process.env.REACT_APP_TYPE

interface ShopProps {
  isDesktop?: boolean
}

export type PopupState = {
  type: 'scanner'
} | {
  type: 'openShop'
} | {
  type: 'shopItem'
  data: shop.ShopItem
} | {
  type: 'delivery'
  data: shop.ShopItem
} | {
  type: 'rental'
  data: shop.RentalProduct
}

const Shop: React.FC<ShopProps> = ({ isDesktop = false }) => {
  const { cartId, warehouseId, equipmentId, categoryId } = useShopParams()
  const basket = useShopItems({ warehouseId, equipmentId, cartId })
  const { popup, openPopup } = usePopupState<PopupState>()
  const recentlySelectedDeliveries = useReduxSelector(selectBasketDeliveries)
  const [isPhysicalShop, setPhysicalShop] = useState(cartId ? false : Object.keys(basket).length === 0)
  const [selectedCategoryId, selectCategoryId] = useState(categoryId || '')
  const { isLoading, data } = useShopData(selectedCategoryId ? undefined : selectCategoryId)
  const rentalProductsCount = data?.reduce((count, category) => count + category.products.filter(isRentalProduct).length, 0) || 0

  const { go } = useHistory()
  const dispatch = useReduxDispatch()
  const { t, lang } = useTranslation('shop')
  const title = type === 'mm' ? 'MagShop' : t('title')

  const listRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const categoriesRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (data?.length && selectedCategoryId) {
      const timeout = setTimeout(() => {
        if (!listRef.current) return
        const ref = Array.from(listRef.current.children).find(child => child.id === selectedCategoryId)
        if (isDesktop) {
          ref?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else if (ref) {
          const offset = (categoriesRef.current?.clientHeight || 60)
          const top = (ref as HTMLDivElement).offsetTop - (cartId ? offset : (1.5 * offset))
          wrapperRef.current?.scrollTo({ top, behavior: 'smooth' })
        }
      })
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [cartId, data?.length, isDesktop, selectedCategoryId])

  return (
    <div
      ref={wrapperRef}
      className='shop__wrapper'>
      {isDesktop
        ? <div className='title'>
          <div>{title}</div>
        </div>
        : <>
          {warehouseId && <HeaderNumber
            warehouseId={warehouseId}
          />}
          {!cartId && <div className='shop__wrapper__header center'>
            <span>{title}</span>
          </div>}
        </>}

      {(() => {
        switch (popup?.type) {
          case 'scanner': return <ScannerPopup />
          case 'openShop': return <OpenShopPopup
            showScanner={() => {
              openPopup({ type: 'scanner' }, true)
            }} />
          case 'shopItem': return <ShopItemPopup
            {...popup.data}
            cartId={cartId}
            isEquipment={!!equipmentId}
            checkPrice={basket?.checkPrice && cartId ? basket.checkPrice[cartId] : undefined}
            basketItem={basket?.list?.[popup.data.id]}
            equipments={basket?.equipment}
            isPhysicalShop={isPhysicalShop}
            openPopup={openPopup}
          />
          case 'rental': return <RentalProductPopup
            product={popup.data}
            basketItem={basket?.rentals?.[popup.data.id]}
            cartId={cartId}
            warehouseId={warehouseId}
            showPhoto={rentalProductsCount === 1}
          />
          case 'delivery': return <DeliveryPopup
            // placeAddress={placeAddress}
            // delivery={productDetails?.delivery}
            productName={popup.data.name}
            price={popup.data.grossPrice}
            deliveryMethods={popup.data.deliveryMethods}
            recentlySelectedDeliveries={recentlySelectedDeliveries}
            setRecentlySelectedDeliveries={(getState) => {
              const state = typeof getState === 'function' ? getState(recentlySelectedDeliveries) : getState
              dispatch(updateDelivery(state))
            }}
            cancelAction={() => {
              go(-1)
            }}
            callback={values => {
              // // update
              // if (productDetails?.cartId) {
              //   dispatch(updateBasketItem({ identifier: productDetails.cartId, values }))

              //   // add new
              // } else {
              const value: basic.Form = {
                type: BASKET_TYPES.shop,
                id: popup.data.id,
                name: popup.data.name,
                object: popup.data.object,
                categoryId: popup.data.category,
                image: popup.data.photos?.[0]?.public_path || placeholder,
                ...values
              }
              if (warehouseId || cartId) {
                value.related = [warehouseId
                  ? {
                    object: 'Warehouse',
                    id: warehouseId
                  } : {
                    object: 'Warehouse',
                    cartId
                  }]
              }
              dispatch(addItemToBasket(value))
              // }
              go(-2)
            }}
          />
          default: return null
        }
      })()}

      <section className='--animated'>
        <div className='shop'>
          {!cartId && <div className='shop__header'>
            <div className='shop__header__info__wrapper'>
              <div className='shop__header__info'>
                {t(isPhysicalShop
                  ? 'equipYourWarehouseWithAccessoriesThatCanSignificantlyIncreaseItsFunctionality'
                  : 'buyItemsWithDeliveryToYourHomeOrYard'
                )}
              </div>
            </div>
            <div className='shop__header__menu'>
              <div
                className={`shop__header__menu__item${isPhysicalShop ? ' --selected' : ''}`}
                onClick={() => {
                  setPhysicalShop(true)
                }}>
                {t('availableInTheYard')}
              </div>
              <div
                className={`shop__header__menu__item${!isPhysicalShop ? ' --selected' : ''}`}
                onClick={() => {
                  setPhysicalShop(false)
                }}>
                {t('orderWithDelivery')}
              </div>
            </div>
          </div>}

          <div className='shop__main'>
            {data && data.length > 1 && <div
              ref={categoriesRef}
              className='shop__categories'>
              {data.map(({ id, name }) => <span
                key={id}
                className={`shop__categories__item${selectedCategoryId === id ? ' --selected' : ''}`}
                onClick={() => {
                  selectCategoryId(id)
                }}>
                {name}
              </span>)}
            </div>}
            <div className='shop__list__wrapper'>
              <div
                ref={listRef}
                className='list shop__list'>
                {isLoading
                  ? <Loading middleOfPage />
                  : data && data.length > 0
                    ? data.map(({ id, name, products }) => <Fragment key={id}>
                      <h2 id={id}>{name}</h2>
                      {products.map((product) => {
                        if (isRentalProduct(product)) {
                          return <RentalProductTile
                            key={product.id}
                            product={product}
                            basketItem={basket?.rentals?.[product.id]}
                            lang={lang}
                            onClick={() => {
                              openPopup({ type: 'rental', data: product })
                            }}
                          />
                        }
                        const { id, name, grossPrice, category, photos, monthlyGrossPrice, description, piecesLeft, maxAmount } = product
                        const selected = equipmentId ? basket.equipment?.[id] : basket.list?.[id]
                        return <div
                          key={id}
                          id={category}
                          className='list__element --big shop__list__item'
                          onClick={() => {
                            openPopup({ type: 'shopItem', data: product })
                          }}>
                          <div className='shop__list__item__photo'>
                            <img
                              src={photos?.[0]?.public_path || placeholder}
                              alt='placeholder'
                            />
                          </div>
                          <div className='shop__list__item__content'>
                            <div>
                              <div className='shop__list__item__title'>{name}</div>
                              {monthlyGrossPrice
                                ? <div className='shop__list__item__subTitle'>{displayPrice(monthlyGrossPrice)} / {t('monthly')}</div>
                                : <div className='shop__list__item__subTitle'>{displayPrice(grossPrice)} / {t('once')}</div>}
                            </div>
                            <div className='shop__list__item__text'>{description}</div>
                            <div className='group'>
                              {piecesLeft && piecesLeft < 5
                                ? <div className='shop__list__item__warning'>
                                  {t('left')} {piecesLeft === 1 ? t('lastPiece') : `${piecesLeft} ${t('pieces')}!`}
                                </div>
                                : <div />}
                              {selected && (equipmentId ? selected?.quantity : true) && !isPhysicalShop
                                ? equipmentId
                                  ? <div
                                    className='shop__list__item__basket center --gap1'
                                    onClick={e => {
                                      e.stopPropagation()
                                    }}>
                                    <span
                                      className={`shop__list__item__basket__button${(!selected || selected.quantity === 0) ? ' --disabled' : ''}`}
                                      onClick={() => {
                                        const couldBeRemoved = selected?.quantity === 1 && !basket.checkPrice?.equipment?.some((e: shop.EquipmentItem) => e.id === id && e.quantity > 0)
                                        if (couldBeRemoved) {
                                          const { [id]: _, ...equipment } = basket.equipment!
                                          dispatch(updateBasketItem({
                                            identifier: cartId,
                                            values: { equipment }
                                          }))

                                        } else if (selected.quantity > 0) {
                                          dispatch(updateBasketItem({
                                            identifier: cartId,
                                            values: { equipment: { ...basket.equipment, [id]: { ...basket.equipment![id], quantity: selected.quantity - 1 } } }
                                          }))
                                        }
                                      }}>
                                      -
                                    </span>
                                    <span className='shop__list__item__basket__amount --secFont'>{selected.quantity}</span>
                                    <span
                                      className={`shop__list__item__basket__button${selected.quantity >= maxAmount ? ' --disabled' : ''}`}
                                      onClick={() => {
                                        if (selected.quantity < maxAmount) {
                                          dispatch(updateBasketItem({
                                            identifier: cartId,
                                            values: { equipment: { ...basket.equipment, [id]: { ...basket.equipment![id], quantity: selected.quantity + 1 } } }
                                          }))
                                        }
                                      }}>
                                      +
                                    </span>
                                  </div>
                                  : <div
                                    className='shop__list__item__basket center --gap1'
                                    onClick={e => {
                                      e.stopPropagation()
                                    }}>
                                    <span
                                      className='shop__list__item__basket__button'
                                      onClick={() => {
                                        if (selected.amount > 1) {
                                          dispatch(updateBasketItem({ identifier: selected.identifier, values: { amount: selected.amount - 1 } }))
                                        } else {
                                          dispatch(removeItemFromBasket({ removeIdentifiers: [selected.identifier] }))
                                        }
                                      }}
                                    >-</span>
                                    <span className='shop__list__item__basket__amount --secFont'>{selected.amount}</span>
                                    <span
                                      className={`shop__list__item__basket__button${selected.amount >= maxAmount ? ' --disabled' : ''}`}
                                      onClick={() => {
                                        if (selected.amount < maxAmount) {
                                          dispatch(updateBasketItem({ identifier: selected.identifier, values: { amount: selected.amount + 1 } }))
                                        }
                                      }}
                                    >+</span>
                                  </div>
                                : <div className='shop__list__item__iconWrapper center'>
                                  <i className='icon icon--arrow-right' />
                                </div>}
                            </div>
                          </div>
                        </div>
                      })}
                    </Fragment>)
                    : <Error
                      text={t('noItems')}
                    />}
              </div>
            </div>
            {!isLoading && data && (Object.keys((equipmentId ? basket.equipment : basket.list) || {}).length > 0 || (!equipmentId && Object.keys(basket.rentals || {}).length > 0)) && !isPhysicalShop && <ArrowBtn
              enlarged
              className='--sticky'
              text={t(equipmentId ? 'ready' : 'order')}
              action={() => {
                if (cartId) {
                  go(-1)
                } else {
                  openPopup({ type: 'basket' })
                }
              }}
            />}
          </div>
        </div>
      </section>
    </div>
  )
}

const isRentalProduct = (product: shop.Product): product is shop.RentalProduct =>
  (product as shop.RentalProduct).type === 'rental'

interface RentalProductTileProps {
  product: shop.RentalProduct
  basketItem?: basic.Form
  lang: string
  onClick: basic.BasicFunc
}

/** kafel wynajmu krótkoterminowego (auto) na liście sklepu: zdjęcie, nazwa, cena „od", dostępność teraz */
const RentalProductTile: React.FC<RentalProductTileProps> = ({ product, basketItem, lang, onClick }) => {
  const { t } = useTranslation('shop.rental')
  const { shortTerm, availability, vehicle, description, warehousePhoto, samePlace, placeName } = product
  const fromDuration = shortTerm ? getDefaultDuration(shortTerm) : undefined
  const fromPrice = shortTerm && fromDuration ? estimatePrice(shortTerm, fromDuration) : undefined

  return (
    <div
      id={product.id}
      className='list__element --big shop__list__item shopRental__item'
      onClick={onClick}>
      <div className='shop__list__item__photo'>
        <img
          src={warehousePhoto || placeholder}
          alt={product.name}
        />
      </div>
      <div className='shop__list__item__content'>
        <div>
          <div className='shop__list__item__title'>{getRentalProductName(product)}</div>
          {fromPrice && fromDuration && <div className='shop__list__item__subTitle'>
            {t('priceFrom').replace('{price}', displayPrice(fromPrice.gross)).replace('{duration}', durationLabel(fromDuration, lang))}
          </div>}
        </div>
        <div className='shop__list__item__text'>
          {basketItem
            ? formatWindowRange(basketItem.startDateTime, basketItem.durationMinutes, basketItem.endDateTime, lang)
            : [vehicle?.registrationNumber, vehicle?.cargoDimensions ? `${t('cargo')}: ${vehicle.cargoDimensions}` : undefined, samePlace === false && placeName ? t('pickupAt').replace('{place}', placeName) : undefined, description].filter(Boolean).join(' · ')}
        </div>
        <div className='group'>
          {basketItem
            ? <div className='shopRental__badge --inCart'>{t('inCart')}</div>
            : availability
              ? <div className={`shopRental__badge --${availability.availableNow ? 'free' : 'busy'}`}>{availability.label}</div>
              : <div />}
          <div className='shop__list__item__iconWrapper center'>
            <i className='icon icon--arrow-right' />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Shop